import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
import { element } from 'prop-types';

// ----------------------------------------------------------------------
const EndUserPage = lazy(() => import('src/pages/dashboard/users/endUser/all'));
const EndUserDocumentSubmissionPage = lazy(() => import('src/pages/dashboard/users/endUser/enduser-document-submissions'));
const EndUserInsightsPage = lazy(() => import('src/pages/dashboard/users/endUser/end-user-insights'));
const VendorPage = lazy(() => import('src/pages/dashboard/users/vendor/all'));
const VendorInsightsPage = lazy(() => import('src/pages/dashboard/users/vendor/vendor-insights'));
const StaffPage = lazy(() => import('src/pages/dashboard/users/staff/all'));
const StaffRolesPage = lazy(() => import('src/pages/dashboard/users/staff/role'));
const PermissionsPage = lazy(() => import('src/pages/dashboard/users/staff/permissions'));

const VehicleListPage = lazy(() => import('src/pages/dashboard/vehicle/all'));
const VehicleTypePage = lazy(() => import('src/pages/dashboard/vehicle/type'));
const VehicleModelPage = lazy(() => import('src/pages/dashboard/vehicle/model'));
const VehicleUtilitiesPage = lazy(() => import('src/pages/dashboard/vehicle/utilities'));
const VehicleListingRequestPage = lazy(() => import('src/pages/dashboard/vehicle/listingRequest'));
const VehicleMakePage = lazy(() => import('src/pages/dashboard/vehicle/make'));
const VehicleAddOnsPage = lazy(() => import('src/pages/dashboard/vehicle/addOns'));
const VehicleInsightsPage = lazy(() => import('src/pages/dashboard/vehicle/vehicle-insights'));

const BookingsPage = lazy(() => import('src/pages/dashboard/bookings/bookings'));
const ZeroBookingVendorsPage = lazy(() => import('src/pages/dashboard/bookings/zeroBookingVendors'));
const BookingsSharedMasterDataPage = lazy(() => import('src/pages/dashboard/bookings/bookingsSharedMasterData'));
const BookingsVehicleMakeMasterDataPage = lazy(() => import('src/pages/dashboard/bookings/bookingsVehicleMakeMasterData'));
const BookingsInsightsPage = lazy(() => import('src/pages/dashboard/bookings/bookings-insights'));

const TripsPage = lazy(() => import('src/pages/dashboard/tours/tours'));
const TripInsightsPage = lazy(() => import('src/pages/dashboard/tours/tour-insights'));

const ClaimsPage = lazy(() => import('src/pages/dashboard/assertions/claims'));

const RevenuePage = lazy(() => import('src/pages/dashboard/cash/revenue'));
const VendorPaymentPage = lazy(() => import('src/pages/dashboard/cash/vendorPayment'));
const RefundPage = lazy(() => import('src/pages/dashboard/cash/refund'));

const CampaignPage = lazy(() => import('src/pages/dashboard/promotion/campaigns-list'));
const PromoPage = lazy(() => import('src/pages/dashboard/promotion/promo-list'));
const EndUserLoyaltyPage = lazy(() => import('src/pages/dashboard/promotion/end-user-loyalty-points'));
const EndUserReferralPage = lazy(() => import('src/pages/dashboard/promotion/end-user-referrals'));
const VendorReferralPage = lazy(() => import('src/pages/dashboard/promotion/vendor-referrals'));
const PromotionMasterDataPage = lazy(() => import('src/pages/dashboard/promotion/promotion-master-data'));

const ChatPage = lazy(() => import('src/pages/dashboard/support/chat'))
const CustomerReportingPage = lazy(() => import('src/pages/dashboard/support/customer-reporting'));
const VendorReportingPage = lazy(() => import('src/pages/dashboard/support/vendor-reporting'));
const VehicleReportingPage = lazy(() => import('src/pages/dashboard/support/vehicle-reporting'));
const NotificationsPage = lazy(() => import('src/pages/dashboard/support/notification'));

const LegalPage = lazy(() => import('src/pages/dashboard/site/legal'));
const FaqPage = lazy(() => import('src/pages/dashboard/site/faq'));

const AccountPage = lazy(() => import('src/pages/dashboard/profile/account'));
const PageProfileSecurity = lazy(() => import('src/pages/dashboard/profile/security'));
// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      {
        path: 'user',
        children: [
          { element: <EndUserPage />, index: true },
          { path: 'end-user', element: <EndUserPage /> },
          { path: 'submissions', element: <EndUserDocumentSubmissionPage /> },
          { path: 'end-user-insights', element: <EndUserInsightsPage /> },
          { path: 'vendor', element: <VendorPage /> },
          { path: 'vendor-insights', element: <VendorInsightsPage /> },
          { path: 'staff', element: <StaffPage /> },
          { path: 'staff-role', element: <StaffRolesPage /> },
          { path: 'permissions', element: <PermissionsPage /> }
        ],
      },
      {
        path: 'vehicle',
        children: [
          { element: <VehicleListPage />, index: true },
          { path: 'list', element: <VehicleListPage /> },
          { path: 'listings-request', element: <VehicleListingRequestPage /> },
          { path: 'type', element: <VehicleTypePage /> },
          { path: 'make', element: <VehicleMakePage /> },
          { path: 'model', element: <VehicleModelPage /> },
          { path: 'utilities', element: <VehicleUtilitiesPage /> },
          { path: 'add-ons', element: <VehicleAddOnsPage /> },
          { path: 'insights', element: <VehicleInsightsPage /> }
        ],
      },
      {
        path: 'bookings',
        children: [
          { element: <BookingsPage />, index: true },
          { path: 'all', element: <BookingsPage /> },
          { path: 'zero-booking-vendors', element: <ZeroBookingVendorsPage /> },
          { path: 'ancillary-fees', element: <BookingsSharedMasterDataPage /> },
          { path: 'make-commissions', element: <BookingsVehicleMakeMasterDataPage /> },
          { path: 'insights', element: <BookingsInsightsPage /> }
        ]
      },
      {
        path: 'tours',
        children: [
          { element: <TripsPage />, index: true },
          { path: 'all', element: <TripsPage /> },
          { path: 'insights', element: <TripInsightsPage /> }
        ]
      },
      {
        path: 'assertion',
        children: [
          { element: <ClaimsPage />, index: true },
          { path: 'claims', element: <ClaimsPage /> }
        ]
      },
      {
        path: 'cash',
        children: [
          { element: <RevenuePage />, index: true },
          { path: 'revenue', element: <RevenuePage /> },
          { path: 'vendor-payment', element: <VendorPaymentPage /> },
          { path: 'refund', element: <RefundPage /> }
        ]
      },
      {
        path: 'promotion',
        children: [
          { element: <CampaignPage />, index: true },
          { path: 'campaign', element: <CampaignPage /> },
          { path: 'promo', element: <PromoPage /> },
          { path: 'end-user-loyalty', element: <EndUserLoyaltyPage /> },
          { path: 'end-user-referrals', element: <EndUserReferralPage /> },
          // { path: 'vendor-referrals', element: <VendorReferralPage /> },
          { path: 'master-data', element: <PromotionMasterDataPage /> }
        ]
      },
      {
        path: 'support',
        children: [
          { element: <ChatPage/>, index: true },
          { path: 'chat', element: <ChatPage/>},
          { path: 'customer-reporting', element: <CustomerReportingPage /> },
          { path: 'vendor-reporting', element: <VendorReportingPage /> },
          { path: 'vehicle-reporting', element: <VehicleReportingPage /> },
          { path: 'alerts', element: <NotificationsPage /> }
        ]
      },
      {
        path: 'site',
        children: [
          { element: <LegalPage />, index: true },
          { path: 'legal', element: <LegalPage /> },
          { path: 'faq', element: <FaqPage /> }
        ]
      },
      {
        path: 'profile',
        children: [
          { element: <AccountPage />, index: true },
          { path: 'account', element: <AccountPage /> },
          { path: 'security', element: <PageProfileSecurity /> }
        ],
      }
    ]
  }
];